import type { ReactNode } from 'react'
import { Button, EmptyState, Skeleton } from '@amglobal/ui-shared'
import { useWorkspaceSession } from '@/features/auth/hooks/use-workspace-session'
import { useHasPermission } from '@/shared/hooks/use-has-permission'

type RequirePermissionProps = {
  perm: string | readonly string[]
  mode?: 'all' | 'any'
  administratorBypass?: boolean
  fallback?: ReactNode
  children: ReactNode
}

export function RequirePermission({
  perm,
  mode = 'all',
  administratorBypass = false,
  fallback,
  children,
}: RequirePermissionProps) {
  const session = useWorkspaceSession()
  const allowed = useHasPermission(perm, mode, { administratorBypass })

  if (session.isLoading) {
    return <Skeleton className="mx-auto mt-8 h-48 max-w-3xl rounded-xl" />
  }

  if (session.isError && !session.snapshot) {
    return (
      <EmptyState
        title="Session unavailable"
        description="We could not load your account details. Refresh the page or sign in again."
        action={
          <Button type="button" variant="secondary" onClick={() => window.location.reload()}>
            Refresh
          </Button>
        }
      />
    )
  }

  if (allowed) return children
  if (fallback) return fallback
  return (
    <EmptyState
      title="Not authorized"
      description="You do not have permission to view this page. Ask your administrator if you need access."
    />
  )
}
